import React, { useContext } from 'react';
import { connect, DispatchProp } from 'react-redux';

import { ItemName, ThemedPencilSvg } from '@dndbeyond/character-components/es';
import {
    CharacterTheme,
    Constants,
    DataOrigin,
    EntityValueLookup,
    InventoryManager,
    HelperUtils,
    Infusion,
    InfusionChoiceLookup,
    InfusionUtils,
    Item,
    ItemUtils,
    RuleData,
    rulesEngineSelectors,
    serviceDataSelectors,
    SnippetData,
    Spell,
    WeaponSpellDamageGroup,
    ContainerLookup,
    PartyInfo,
    CustomItemContract,
    characterActions,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import ItemDetail from '../../../components/ItemDetail';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader } from '../../../components/Sidebar';
import { InventoryManagerContext } from '../../../managers/InventoryManagerContext';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneIdentifierUtils, PaneUtils } from '../../../utils';
import { PaneIdentifiersItem } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    items: Array<Item>;
    weaponSpellDamageGroups: Array<WeaponSpellDamageGroup>;
    ruleData: RuleData;
    snippetData: SnippetData;
    entityValueLookup: EntityValueLookup;
    infusionChoiceLookup: InfusionChoiceLookup;
    identifiers: PaneIdentifiersItem | null;
    isReadonly: boolean;
    proficiencyBonus: number;
    theme: CharacterTheme;
    containerLookup: ContainerLookup;
    inventoryManager: InventoryManager;
    partyInfo: PartyInfo;
}
interface State {
    item: Item | null;
    isCustomizeClosed?: boolean;
}
class ItemPane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { items, identifiers } = this.props;

        if (items !== prevProps.items || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { items, identifiers } = props;

        let foundItem: Item | null | undefined = null;
        if (identifiers !== null) {
            foundItem = items.find((item) => identifiers.id === ItemUtils.getMappingId(item));
        }

        return {
            item: foundItem ? foundItem : null,
            isCustomizeClosed: true,
        };
    };

    handleCustomItemEdit = (adjustments: Record<string, any>): void => {
        const { item } = this.state;
        const { inventoryManager } = this.props;

        if (item === null) {
            return;
        }

        inventoryManager.handleCustomEdit({ item, adjustments });
    };

    handleCustomDataUpdate = (adjustmentType: Constants.AdjustmentTypeEnum, value: any): void => {
        const { item } = this.state;
        const { inventoryManager } = this.props;

        if (item) {
            inventoryManager.handleCustomizationSet({
                item,
                adjustmentType,
                value,
            });
        }
    };

    handleRemoveCustomizations = (): void => {
        const { item } = this.state;
        const { inventoryManager } = this.props;

        if (item) {
            inventoryManager.handleCustomizationsRemove({ item });
        }
    };

    handleDataOriginClick = (dataOrigin: DataOrigin) => {
        const { dispatch } = this.props;

        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);
        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    handleSpellClick = (spell: Spell): void => {
        const { dispatch } = this.props;

        let component = PaneUtils.getSpellComponentInfo(spell);
        if (component.type) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    handleInfusionClick = (infusion: Infusion): void => {
        const { dispatch } = this.props;

        const choiceKey = InfusionUtils.getChoiceKey(infusion);
        if (choiceKey !== null) {
            dispatch(
                sidebarActions.paneHistoryPush(
                    PaneComponentEnum.INFUSION_CHOICE,
                    PaneIdentifierUtils.generateInfusionChoice(choiceKey),
                ),
            );
        }
    };

    openCustomize = () => {
        this.setState({ isCustomizeClosed: !this.state.isCustomizeClosed });
    };

    render() {
        const { item, isCustomizeClosed } = this.state;
        const {
            weaponSpellDamageGroups,
            ruleData,
            snippetData,
            entityValueLookup,
            infusionChoiceLookup,
            isReadonly,
            proficiencyBonus,
            theme,
            containerLookup,
            inventoryManager,
            partyInfo,
        } = this.props;

        if (item === null) {
            return (
                <PaneInitFailureContent errorMessage="That item is no longer in your inventory! Please try again." />
            );
        }

        const canCustomize = inventoryManager.canCustomizeItem(item);

        return (
            <div className="ct-item-pane">
                <SidebarHeader previewImage={ItemUtils.getAvatarUrl(item)}>
                    {/* <ItemName item={item} theme={theme} />
                     */}
                    <div className="ct-container-pane__item-name">
                        <ItemName item={item} theme={theme} />
                        <div className="ct-container-pane__pencil" onClick={this.openCustomize}>
                            <ThemedPencilSvg theme={theme} />
                        </div>
                    </div>
                </SidebarHeader>
                <ItemDetail
                    theme={theme}
                    key={ItemUtils.getUniqueKey(item)}
                    item={item}
                    weaponSpellDamageGroups={weaponSpellDamageGroups}
                    ruleData={ruleData}
                    snippetData={snippetData}
                    onCustomDataUpdate={this.handleCustomDataUpdate}
                    onCustomizationsRemove={this.handleRemoveCustomizations}
                    onDataOriginClick={this.handleDataOriginClick}
                    onSpellClick={this.handleSpellClick}
                    onInfusionClick={this.handleInfusionClick}
                    entityValueLookup={entityValueLookup}
                    infusionChoiceLookup={infusionChoiceLookup}
                    isReadonly={isReadonly}
                    proficiencyBonus={proficiencyBonus}
                    container={HelperUtils.lookupDataOrFallback(
                        containerLookup,
                        ItemUtils.getContainerDefinitionKey(item),
                    )}
                    showCustomize={canCustomize}
                    onPostRemoveNavigation={PaneComponentEnum.EQUIPMENT_MANAGE}
                    partyInfo={partyInfo}
                    onCustomItemEdit={this.handleCustomItemEdit}
                    isCustomizeClosed={isCustomizeClosed}
                />
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        ruleData: rulesEngineSelectors.getRuleData(state),
        items: rulesEngineSelectors.getAllInventoryItems(state),
        weaponSpellDamageGroups: rulesEngineSelectors.getWeaponSpellDamageGroups(state),
        entityValueLookup: rulesEngineSelectors.getCharacterValueLookupByEntity(state),
        snippetData: rulesEngineSelectors.getSnippetData(state),
        infusionChoiceLookup: rulesEngineSelectors.getInfusionChoiceLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        containerLookup: rulesEngineSelectors.getContainerLookup(state),
        partyInfo: serviceDataSelectors.getPartyInfo(state),
    };
}

const ItemPaneContainer = (props) => {
    const { inventoryManager } = useContext(InventoryManagerContext);
    return <ItemPane inventoryManager={inventoryManager} {...props} />;
};

export default connect(mapStateToProps)(ItemPaneContainer);
